import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate
} from "react-router-dom";
import Account from "byzantine/src/Account";
import Dashboard from "../dashboard/Dashboard";
import SavvyMoneyDashboard from "../savvy_money/SavvyMoneyDashboard";
import { AccountContextProvider } from "../contexts/AccountContext";
import MainLayout from "./MainLayout";
import { Institution, LegacyNafUrl } from "../../types";
import ScrollToTopOnRouteChange from "../ScrollToTopOnRouteChange";
import PositivePay from "../positivePay/component";
import ZEscrow from "../zescrow/component";
import SubscriptionManagement from "../subscriptions/SubscriptionManagement";
import CardOffersDashboard from "../CardOffersDashboard";
import DirectDepositPage from "../DirectDeposit";
import { SubscriptionCardsLayout } from "../contexts/SubscriptionCardsContext";
import { DashboardHiddenSectionsProvider } from "../contexts/DashboardHiddenSectionsContext";
import PaymentActivityRoutes, {
  PAYMENT_ACTIVITY_ROUTE
} from "../PaymentActivity/routes";
import { canShowPaymentActivity } from "../PaymentActivity/featureGuards";
import ArrayPlusWidget from "../ArrayPlus/ArrayPlusWidget";
import DualApprovalsRoutes from "../DualApproval/routes";
import TemplatesRoutes from "../Templates/routes";
import { areTemplatesAvailable, TEMPLATES_ROUTE } from "../Templates/constants";
import ReceiptsRoutes, { RECEIPTS_ROUTE } from "../Receipts/routes";

interface MainProps {
  accounts: Account[];
  features: API.Features;
  institution: Institution;
  legacyNafUrls: LegacyNafUrl[];
  deviceIsRemembered: boolean;
  externalAccounts: Account[];
  replyTime: string;
  androidUrl: string;
  appleUrl: string;
  limitsJson: string;
  uatMicrodepAmntOne?: string;
  uatMicrodepAmntTwo?: string;
}

const Main = ({
  accounts,
  features,
  institution,
  legacyNafUrls,
  deviceIsRemembered,
  externalAccounts,
  replyTime,
  androidUrl,
  appleUrl,
  limitsJson,
  uatMicrodepAmntOne,
  uatMicrodepAmntTwo
}: MainProps) => (
  <AccountContextProvider accounts={accounts}>
    <Router basename="/">
      <ScrollToTopOnRouteChange />
      <Routes>
        <Route
          path="/"
          element={
            <MainLayout
              legacyNafUrls={legacyNafUrls}
              deviceIsRemembered={deviceIsRemembered}
              institution={institution}
              accounts={accounts}
            />
          }
        >
          <Route element={<SubscriptionCardsLayout />}>
            {[
              "/",
              "/dashboard",
              "/dashboard/*",
              "/dashboard/linked-accounts/*"
            ].map((path) => (
              <Route
                key={path}
                path={path}
                element={
                  <DashboardHiddenSectionsProvider>
                    <Dashboard
                      features={features}
                      institution={institution}
                      replyTime={replyTime}
                      androidUrl={androidUrl}
                      appleUrl={appleUrl}
                      externalAccounts={externalAccounts}
                      uatMicrodepAmntOne={uatMicrodepAmntOne}
                      uatMicrodepAmntTwo={uatMicrodepAmntTwo}
                    />
                  </DashboardHiddenSectionsProvider>
                }
              />
            ))}

            {/* Subscription routes always rendered to handle graceful empty state */}
            {["cards/subscriptions", "cards/:cardUuid/subscriptions/*"].map(
              (path) => (
                <Route
                  key={path}
                  path={path}
                  element={<SubscriptionManagement />}
                />
              )
            )}

            {/* Card Offers Dashboard Routes */}
            {features.card_linked_offers && (
              <>
                {["cards/offers", "cards/:cardUuid/offers"].map((path) => (
                  <Route
                    key={path}
                    path={path}
                    element={<CardOffersDashboard />}
                  />
                ))}
              </>
            )}
          </Route>
          {features.olb_enable_credit_monitor && (
            <Route
              path="/credit_monitor"
              element={<SavvyMoneyDashboard features={features} />}
            />
          )}
          <Route path="/positive_pay" element={<PositivePay />} />
          <Route path="/zescrow" element={<ZEscrow />} />
          {features.direct_deposit && (
            <Route
              path="/direct_deposit"
              element={
                <div className="transfer-page-layout">
                  <div className="transfer-page">
                    <DirectDepositPage />
                  </div>
                </div>
              }
            />
          )}
          {features.array_plus_credit_reporting && (
            <Route
              path="/credit_report"
              element={<ArrayPlusWidget fullScreen />}
            />
          )}
          {canShowPaymentActivity(features) ? (
            <Route
              path={`/${PAYMENT_ACTIVITY_ROUTE}/*`}
              element={
                <PaymentActivityRoutes
                  limitsJson={limitsJson}
                  features={features}
                />
              }
            />
          ) : null}
          <Route path={"/approvals/*"} element={<DualApprovalsRoutes />} />
          {features.receipts_enabled ? (
            <Route path={`/${RECEIPTS_ROUTE}/*`} element={<ReceiptsRoutes />} />
          ) : null}
          {areTemplatesAvailable(features) ? (
            <Route
              path={`/${TEMPLATES_ROUTE}/*`}
              element={
                <TemplatesRoutes limitsJson={limitsJson} features={features} />
              }
            />
          ) : null}
          {/* Catch-all route to redirect to dashboard */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </Router>
  </AccountContextProvider>
);

export default Main;
